/**
 * WorkerRequests — list of process requests submitted by the worker,
 * with a right-side drawer showing the submitted form_data
 * read-only against the parent ProcessDefinition.form_schema.
 *
 * P0.4 follow-up rewrite (2026-05-16): the legacy table only showed
 * title / submitted / status, with no way to inspect what the
 * worker actually filled in. With the new list_requests joining
 * form_schema and ProcessRequest.data already carrying the
 * submitted payload, the drawer can render the same form fields
 * the admin configured, populated from the worker's submission.
 *
 * Mirrors the WorkerTasks drawer pattern (480px right-side) so
 * tasks-out and requests-in share form vocabulary. Drawer is
 * deliberately read-only here — workers can't edit a submitted
 * request from this surface; they'd cancel it (out of scope) or
 * submit a new one.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton,
  Paper, Snackbar, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import WorkerListToolbar, { matchesSubmittedFilter } from '../../components/worker/WorkerListToolbar';
import { meApi } from '../../api';
import { useTranslation } from 'react-i18next';
import { activeLocale } from '../../utils/datetime';
import { seedStatusFromUrl } from '../../utils/seedStatusFromUrl';
import type { ProcessAttachmentMeta } from './ProcessFormResourceValue';

import { getSystemRequestTitle } from '../Processes/processDisplay';

interface FormFieldProperty {
  label?: string;
  placeholder?: string;
  required?: boolean;
  options?: string[] | { label: string; value: string }[];
  [k: string]: unknown;
}

interface FormField {
  id?: string;
  type?: string;
  label?: string;
  required?: boolean;
  properties?: FormFieldProperty;
}

/**
 * A HAND-COPIED subset of `ProcessRequestResponse`, and that is a drift risk rather than a design: the
 * generated `schema.d.ts` already describes this shape, and adding `instance_number` to the backend left
 * this interface silently one field behind until `bun run build` rejected the render (svdy-rules §10.5b).
 * Left as an interface for now because narrowing it to `Schemas['ProcessRequestResponse']` changes the
 * nullability of ~10 fields this page reads, which is a separate change with its own test surface.
 */
interface RequestRow {
  id: string;
  definition_id?: string | null;
  instance_id?: string | null;
  /** The number a requester quotes ("2026-0042"), carried from the linked instance. */
  instance_number?: string | null;
  title: string;
  status: string;
  priority?: string | null;
  data?: Record<string, unknown> | null;
  submitted_at?: string | null;
  current_step?: string | null;
  current_approver_id?: string | null;
  due_date?: string | null;
  attachments_count?: number;
  attachments?: ProcessAttachmentMeta[];
  comments_count?: number;
  form_schema?: { fields?: FormField[] } | null;
  created_at?: string;
}

const fieldKey = (f: FormField, idx: number): string =>
  (f.properties?.label as string) || f.label || f.id || `field_${idx}`;

const _fieldLabel = (f: FormField, idx: number): string =>
  (f.properties?.label as string) || f.label || fieldKey(f, idx);

const _fieldOptions = (f: FormField): { label: string; value: string }[] => {
  const opts = f.properties?.options;
  if (!Array.isArray(opts)) return [];
  return opts.map((o) =>
    typeof o === 'string' ? { label: o, value: o } : o,
  );
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'worker:requestDetail.status_draft',
  submitted: 'worker:requestDetail.status_submitted',
  in_review: 'worker:requestDetail.status_in_review',
  approved: 'worker:requestDetail.status_approved',
  rejected: 'worker:requestDetail.status_rejected',
  completed: 'worker:requestDetail.status_completed',
  cancelled: 'worker:requestDetail.status_cancelled',
  withdrawn: 'worker:requestDetail.status_withdrawn',
  delegated: 'worker:requestDetail.status_delegated',
  running: 'worker:requestDetail.status_running',
  failed: 'worker:requestDetail.status_failed',
};

const statusColor = (s: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  if (s === 'approved' || s === 'completed') return 'success';
  if (s === 'rejected' || s === 'failed') return 'error';
  if (s === 'submitted' || s === 'in_review' || s === 'running' || s === 'pending') return 'warning';
  if (s === 'cancelled' || s === 'withdrawn' || s === 'delegated') return 'info';
  return 'default';
};

const WorkerRequests: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<RequestRow[]>([]);
  const [loadError, setLoadError] = useState(false);
    /**
   * The status filter can be SEEDED from `?status=`, because a click on a COUNT must land on the list
   * that count describes. The Overview's pending card links here with `?status=pending`; the sidebar
   * entry links without it and keeps this page's own default.
   *
   * Seed only — the toolbar owns the value afterwards. Syncing both ways would make the URL and the
   * control two sources of truth for one piece of state (svdy-rules §10.5b), and the `?compose=1`
   * precedent in this repo consumes-and-clears for the same reason.
   */
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'completed' | 'rejected'>(() => {
    const seeded = seedStatusFromUrl<'all' | 'pending' | 'completed' | 'rejected'>('all');
    return seeded;
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const params: Record<string, string> = {};
      if (searchQuery.trim()) params.q = searchQuery.trim();
      const data = (await meApi.requests.list(params)) as RequestRow[];
      setRequests(Array.isArray(data) ? data : []);
    } catch {
      // Preserve last-known-good rows; an API failure is not an empty inbox.
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const filtered = useMemo(
    // ONE predicate, shared with the phone and with the two sibling surfaces that use the same chip set.
    // The hand-rolled version here grouped `withdrawn` and `cancelled` under `rejected`, so a request the
    // requester took back was described to them as refused (owner, 2026-09-10).
    () => requests.filter((r) => matchesSubmittedFilter(r.status, statusFilter)),
    [requests, statusFilter],
  );
  if (loading && requests.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      py: embedded ? 0 : '12px',
      width: '100%',
    }}>
      {/* Header. Suppressed when embedded in WorkerRequestsHub, which owns the one page
          title — the same `embedded` contract WorkerApprovals uses inside its hub. The
          filter row and the primary action stay: they belong to THIS tab's list, not to the
          hub, and hoisting them would make them lie about the other tab. */}
      <Box sx={{
        width: '100%', display: 'flex',
        justifyContent: 'space-between', alignItems: 'center',
        mb: 1.5, flexWrap: 'wrap', gap: 1,
      }}>
        <Box>
          {!embedded && (
            <>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('worker:requests.title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('worker:requests.requestsSubtitle')}
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {/* ONE toolbar, ONE vocabulary. This page said Completed / Closed while the attendance
          tab said Approved / Rejected — the same question answered in two words, on two tabs of
          one entry. The vocabulary is now chosen by what the list IS ("things I submitted"),
          which is what stops a module-shaped answer from drifting again. */}
      <WorkerListToolbar
        set="submitted"
        value={statusFilter}
        onChange={(v) => setStatusFilter(v as typeof statusFilter)}
        search={searchQuery}
        onSearchChange={setSearchQuery}
        action={(
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate('/me/requests/new')}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
          >
            {t('worker:requests.newRequest')}
          </Button>
        )}
      />

      {loadError && (
        <Alert
          severity="error"
          sx={{ width: '100%', mb: 1.5 }}
          action={<Button color="inherit" size="small" onClick={fetchRequests}>{t('common:actions.retry')}</Button>}
        >
          {t('common:feedback.loadFailed')}
        </Alert>
      )}

      {filtered.length === 0 && !loadError ? (
        <Paper elevation={0} sx={{
          width: '100%', p: 5, textAlign: 'center',
          borderRadius: 2, border: '1px solid', borderColor: 'divider',
        }}>
          <Typography variant="body2" color="text.secondary">
            {statusFilter === 'all' ? t('worker:requests.noRequestsYet') : t('worker:requests.noRequestsMatch')}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            {t('worker:requests.noRequestsHint')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{
          width: '100%', borderRadius: 2, overflow: 'hidden',
          border: '1px solid', borderColor: 'divider', boxShadow: 'none',
        }}>
          <Table size="small">
            <TableHead sx={{
              bgcolor: 'grey.100',
              '& .MuiTableCell-root': { fontWeight: 600, fontSize: '0.75rem' },
            }}>
              <TableRow>
                <TableCell>{t('worker:requests.colTitle')}</TableCell>
                <TableCell>{t('worker:requests.colForm')}</TableCell>
                <TableCell>{t('worker:requests.colSubmitted')}</TableCell>
                <TableCell align="center">{t('worker:requests.colStatus')}</TableCell>
                <TableCell align="center" sx={{ width: 56 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((r) => {
                const fieldCount = r.form_schema?.fields?.length ?? 0;
                return (
                  <TableRow
                    key={r.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/me/requests/${r.id}`)}
                  >
                    <TableCell>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {getSystemRequestTitle(t, r, r.title) || '—'}
                        {/* Two `Procurement Request` rows used to differ only by date. The number is the
                            row's identity; no truncated-id fallback, deliberately. */}
                        {r.instance_number ? (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            {r.instance_number}
                          </Typography>
                        ) : null}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        {fieldCount > 0 ? (
                          <Chip
                            label={t('worker:requests.fieldCount', { count: fieldCount })}
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: 1, height: 18, fontSize: '0.65rem' }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                        {(r.attachments?.length ?? r.attachments_count ?? 0) > 0 && (
                          <Chip
                            label={t('worker:requests.attachmentCount', { count: r.attachments?.length ?? r.attachments_count ?? 0 })}
                            size="small"
                            variant="outlined"
                            color="info"
                            sx={{ borderRadius: 1, height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {r.submitted_at || r.created_at
                          ? new Date(r.submitted_at ?? r.created_at!).toLocaleDateString(activeLocale())
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={t(STATUS_LABEL[r.status ?? 'pending'] ?? '', { defaultValue: r.status ?? 'Pending' })}
                        size="small"
                        color={statusColor(r.status ?? 'pending')}
                        variant="outlined"
                        sx={{ borderRadius: 1, height: 18, fontSize: '0.65rem' }}
                      />
                    </TableCell>
                    {/* Explicit View button (Path C 2026-05-22) — the
                        whole row is still clickable for affordance, but
                        a discoverable icon makes the action obvious to
                        first-time users who might not realize a row
                        click navigates. */}
                    <TableCell align="center">
                      <Tooltip title={t('worker:requests.viewDetails')}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/me/requests/${r.id}`);
                          }}
                          sx={{ color: 'text.secondary' }}
                        >
                          <ViewIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkerRequests;
